import { useEffect, useRef, useState } from 'react';
import { Text, Button, Card, Subtitle1, Switch, } from '@fluentui/react-components';
import { CheckmarkCircleRegular, DismissRegular, ErrorCircleRegular, QuestionRegular } from "@fluentui/react-icons";
import {
    MessageBar,
    MessageBarActions,
    MessageBarTitle,
    MessageBarBody,
    MessageBarGroup,
    MessageBarIntent,
    makeStyles,
    shorthands
} from "@fluentui/react-components";
import { PublicClientApplication, InteractionRequiredAuthError, AuthError, AuthenticationResult } from "@azure/msal-browser";

import { msalConfigGraph, loginRequestGraph } from '../authConfigGraph';
import { useSignInState } from '../signInState';
import authErrorHandling from '../authErrorHandling';
import useFetch from '../../Hooks/useFetchAPI';
import { FeatureSkeleton } from '../LoadingSkeletons';
import ModuleConfigTable from './ModuleConfigTable';

const intents: MessageBarIntent[] = ["info", "warning", "error", "success"];

interface NotificationMessage {
    intent: MessageBarIntent;
    id: number;
    message: string;
};

const useStyles = makeStyles({
    messageBarGroup: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...shorthands.overflow("auto"),
    },
});


const ModuleLenovoWarranty = ({ tenantConfigured }: { tenantConfigured: boolean }) => {
    const styles = useStyles();
    const [authError, setauthError] = useState<string>('');
    const [connectorTestError, setConnectorTestError] = useState<boolean>(false);
    const { signInState } = useSignInState();
    const featureName = 'IntuneReporting';
    const connectorName = 'LenovoWarranty';
    const counterRef = useRef(0);

    const [featureConfigData, setFeatureConfigData] = useState<LenovoWarrantyFeatureConfigData[] | null>(null);
    const [lenovoWarrantyConfigItems, setLenovoWarrantyConfigItems] = useState<ModuleConfigTableProps[]>([]);
    const { fetchAPI: fetchConfig, apiData, isLoading, serverError } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: updateDatabase, apiData: submitData, isLoading: isLoadingSubmit, serverError: serverErrorSubmit } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: testConfig, apiData: testConfigData, isLoading: isLoadingTestConfig, serverError: serverErrorTestConfig } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: enableDataSync, apiData: enableDataSyncData, isLoading: isLoadingEnableDataSync, serverError: serverErrorEnableDataSync } = useFetch({ scope: 'AdminUser' })

    const [messages, setMessages] = useState<NotificationMessage[]>([]);

    const addMessage = (message: string, intent: MessageBarIntent) => {
        const newMessage = { intent, id: counterRef.current++, message };
        setMessages((s) => [newMessage, ...s]);
    };

    const dismissMessage = (messageId: number) =>
        setMessages((s) => s.filter((entry) => entry.id !== messageId)
        );


    useEffect(() => {
        const fetchConfigAsync = async () => {
            await fetchConfig({ url: `admin/getfeatureconfig?featureName=${featureName}`, method: 'GET' });
        };

        fetchConfigAsync()
    }, [signInState.currentCustomerID]);

    const msalInstanceGraph = new PublicClientApplication(msalConfigGraph(process.env.REACT_APP_CLIENT_GRAPH_APP_ID!));

    const handleAppRegConsent = async () => {
        await msalInstanceGraph.initialize();
        await msalInstanceGraph.loginPopup(loginRequestGraph)
            .then(response => {
                // Handle the response
                setauthError('');
            })
            .catch(error => {
                // Handle the error
                console.log('[consentAppReg] Error:', error);
                setauthError(authErrorHandling(error));
                addMessage(authErrorHandling(error), 'error');
            });
    };

    const updateConfiguration = async (key: string, value: string | number | boolean | Date) => {
        console.log('[ModuleConfig-LenovoWarranty] Updating configuration:', key, value);
        const encodedValue = encodeURIComponent(value.toString());
        await updateDatabase({ url: `admin/updatefeatureconfig?featureName=${featureName}&${key}=${encodedValue}`, method: 'PUT' });

        setFeatureConfigData((prev) => {
            if (!prev) return prev; // Return unchanged if prev is falsy
            return prev.map(item =>
                item.hasOwnProperty(key) ? { ...item, [key]: value } : item
            );
        });
        setLenovoWarrantyConfigItems((prev) => {
            if (!prev) return prev; // Return unchanged if prev is falsy
            return prev.map(item =>
                item.hasOwnProperty(key) ? { ...item, [key]: value } : item
            );
        });

        if (key === 'LenovoWarrantyEnabled') {
            await enableDataSync({ url: 'admin/enabledatasync', method: 'POST', body: { LenovoWarrantyEnabled: value, } });
        }

    }

    useEffect(() => {
        if (apiData) {
            testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
        }
    }, []);

    const testConfiguration = async () => {
        console.log('[ModuleConfig-LenovoWarranty] Testing configuration');
        setMessages([]);
        await testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
    }

    useEffect(() => {
        if (testConfigData) {
            (async () => {
                console.log('[ModuleConfig-LenovoWarranty] testResult:', testConfigData);
                if (testConfigData?.result) {
                    console.log('[ModuleConfig-LenovoWarranty] Configuration test successful');
                    updateConfiguration('LenovoStatus', 'Configured');
                    updateConfiguration('LenovoStatusMessage', 'Tested Successfully');
                    setConnectorTestError(false);
                }
                else {
                    console.log('[ModuleConfig-LenovoWarranty] Configuration test failed');
                    updateConfiguration('LenovoStatus', 'Not Configured');
                    updateConfiguration('LenovoStatusMessage', 'Test Failed');
                    updateConfiguration('LenovoWarrantyEnabled', false);
                    setMessages([]);
                    setConnectorTestError(true);
                    switch (testConfigData.error) {
                        case 'HTTP error! status: 401': {
                            addMessage('HTTP error! status: 401 - Unauthorized. Please check your API keys.', 'error');
                            break;
                        }
                        default:
                            addMessage(testConfigData.error !== undefined ? testConfigData.error.toString() : 'Unknown error, please check your API keys.', 'error');
                    }
                }
            })();
        }
    }, [testConfigData]);

    useEffect(() => {
        if (!isLoading && !serverError && apiData) {
            console.log('[ModuleConfig-LenovoWarranty] apiData', apiData)
            if (apiData && Array.isArray(apiData.result) && apiData.result.length > 0 && (apiData as unknown as { result: LenovoWarrantyFeatureConfigData[] }).result) {
                let featureConfigDataResult: LenovoWarrantyFeatureConfigData[] | null = null;
                let lenovoWarrantyConfigItemsResult: ModuleConfigTableProps[] = [];
                featureConfigDataResult = (apiData as unknown as { result: LenovoWarrantyFeatureConfigData[] }).result;
                // console.log('featureConfigDataResult: ', featureConfigDataResult);
                setFeatureConfigData(featureConfigDataResult);

                if (Array.isArray(featureConfigDataResult)) {
                    lenovoWarrantyConfigItemsResult = featureConfigDataResult.flatMap(item => ([
                        { key: 'LenovoWarrantyAPIKey', displayName: 'Lenovo Warranty API Key', value: item.LenovoWarrantyAPIKey, secure: true }
                    ]));
                }
                // console.log('IntuneReportingConfigItemsResult:', IntuneReportingConfigItemsResult);
                setLenovoWarrantyConfigItems(lenovoWarrantyConfigItemsResult);
            }
            else {
                console.log('[ModuleConfig-LenovoWarranty] apiData is empty');
                let featureConfigDataResult: LenovoWarrantyFeatureConfigData[] = [{
                    AppFeatureID: 1,
                    LenovoWarrantyEnabled: false,
                    LenovoWarrantyAPIKey: '',
                    SyncSchedule4: '',
                    LenovoStatus: '',
                    LenovoStatusMessage: '',
                    LenovoWarrantyLastSyncEnd: null,
                    LenovoWarrantyLastSyncDuration: null
                }];
                setFeatureConfigData(featureConfigDataResult);
                let lenovoWarrantyConfigItemsResult: ModuleConfigTableProps[] = [
                    { key: 'LenovoWarrantyAPIKey', displayName: 'Lenovo Warranty API Key', value: '', secure: true }
                ];
                setLenovoWarrantyConfigItems(lenovoWarrantyConfigItemsResult);
            }
        }
    }, [isLoading, serverError, apiData]);

    return (
        <div className="LenovoWarrantyConfig" style={{ margin: '20px', maxWidth: '1200px' }}>
            {isLoading && !serverError && !apiData &&
                <Card>
                    <Subtitle1>Lenovo Warranty Connector Setup</Subtitle1>
                    <FeatureSkeleton />
                </Card>

            }
            {!isLoading && !serverError && featureConfigData && apiData && lenovoWarrantyConfigItems &&
                <Card>
                    <Subtitle1>Lenovo Warranty Connector Setup</Subtitle1>
                    <Text>Contact your Lenovo Account Manager for your Warranty API Keys.</Text>
                    <br />
                    <Switch
                        key={"LenovoWarrantyEnabled"}
                        label={"Connector " + (featureConfigData[0].LenovoWarrantyEnabled ? "enabled" : "disabled")}
                        // labelPosition="before"
                        checked={featureConfigData[0].LenovoWarrantyEnabled}
                        disabled={!(
                            featureConfigData[0]?.LenovoWarrantyAPIKey &&
                            featureConfigData[0]?.LenovoStatusMessage === "Tested Successfully" &&
                            tenantConfigured
                        )}
                        onChange={(e) => updateConfiguration("LenovoWarrantyEnabled", e.target.checked)}
                    />

                    <Text>{featureConfigData[0]?.LenovoStatus ? `Connector Status: ${featureConfigData[0]?.LenovoStatus}` : null}</Text>
                    <Text>{featureConfigData[0]?.LenovoStatusMessage ? `Connector Status Message: ${featureConfigData[0]?.LenovoStatusMessage}` : null}</Text>
                    <Text>{featureConfigData[0]?.LenovoWarrantyLastSyncEnd ? `Connector Last Sync End Date: ${new Date(featureConfigData[0]?.LenovoWarrantyLastSyncEnd).toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : `Connector Last Sync End Date: N/A`}</Text>
                    <Text>
                        {featureConfigData[0]?.LenovoWarrantyLastSyncDuration == null
                            ? 'Connector Last Sync Duration: N/A'
                            : `Connector Last Sync Duration: ${featureConfigData[0]?.LenovoWarrantyLastSyncDuration === 0
                                ? '< 1 Min'
                                : `${featureConfigData[0]?.LenovoWarrantyLastSyncDuration} mins`
                            }`}
                    </Text>
                    < ModuleConfigTable<ModuleConfigTableProps[]> data={lenovoWarrantyConfigItems} appFeature={featureName} onUpdate={updateConfiguration} />

                    <div style={{ width: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <Button style={{ width: '100%' }} onClick={testConfiguration}
                            icon={featureConfigData[0].LenovoStatusMessage === 'Tested Successfully'
                                ? <CheckmarkCircleRegular style={{ color: 'green' }} />
                                : <ErrorCircleRegular style={{ color: 'red' }} />}
                        >
                            Test Configuration
                        </Button>
                    </div>
                    {/* <br/> */}
                    {connectorTestError &&
                        <MessageBarGroup className={styles.messageBarGroup}>
                            {messages.map(({ intent, id, message }) => (
                                <MessageBar key={`${intent}-${id}`} intent={intent}>
                                    <MessageBarBody>
                                        <MessageBarTitle>Testing Error:</MessageBarTitle>
                                        {message}
                                    </MessageBarBody>
                                    <MessageBarActions
                                        containerAction={
                                            <Button
                                                onClick={() => dismissMessage(id)}
                                                aria-label="dismiss"
                                                appearance="transparent"
                                                icon={<DismissRegular />}
                                            />
                                        }
                                    />
                                </MessageBar>
                            ))}
                        </MessageBarGroup>
                    }
                </Card>
            }
            {serverError &&
                // Put in nice pretty error handling component when it's created
                <>Server Error: {serverError}</>
            }

        </div>
    );

};

export default ModuleLenovoWarranty;
